/**
 * PublicLeagueStandingsPage — /l/:slug/standings
 * ─────────────────────────────────────────────────────────────────
 * REDESIGNED in the public pages overhaul P3 (2026-06-09).
 * Per-division standings with season picker. GF/GA/DIFF columns
 * render only when the league's public_show_goal_stats toggle is on
 * (the backend strips the fields AND sends show_goal_stats).
 */

import { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import { container, Section, Card, EmptyState, INK, MUTED, LINE, SURFACE } from './pubUi.jsx';

export default function PublicLeagueStandingsPage() {
  const { league, currentSeason, accent } = useOutletContext();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const requestedSeason = searchParams.get('season');

  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState(requestedSeason || currentSeason?.id || '');
  const [standings, setStandings] = useState(null);

  useEffect(() => { document.title = `Standings — ${league.name}`; }, [league.name]);

  useEffect(() => {
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons`)
      .then(({ data }) => {
        const list = data?.seasons || [];
        setSeasons(list);
        setSeasonId(prev => prev || list[0]?.id || '');
      })
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (!seasonId) return;
    let cancelled = false;
    setStandings(null);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/seasons/${seasonId}/standings`)
      .then(({ data }) => { if (!cancelled) setStandings(data); })
      .catch(() => { if (!cancelled) setStandings({ divisions: [] }); });
    return () => { cancelled = true; };
  }, [slug, seasonId]);

  const showGoals = standings?.show_goal_stats !== false;

  return (
    <div style={{ ...container, paddingTop: 36 }}>
      <Section
        eyebrow={standings?.season?.name || ''}
        title="Standings"
        action={seasons.length > 1 && (
          <select value={seasonId} onChange={(e) => setSeasonId(e.target.value)}
            style={{ padding: '10px 14px', borderRadius: 12, border: `1px solid ${LINE}`, background: '#fff', fontSize: 14, fontWeight: 600 }}>
            {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}>
        {standings === null && <div style={{ color: MUTED }}>Loading…</div>}
        {standings?.divisions?.length === 0 && (
          <EmptyState icon="📊">Standings will appear once games have been played.</EmptyState>
        )}
        {standings?.divisions?.map(d => (
          <Card key={d.division_id || 'all'} style={{ marginBottom: 22, overflow: 'hidden' }}>
            {d.division_name && (
              <div style={{ padding: '14px 20px 0', fontWeight: 800, fontSize: 16, color: INK }}>
                {d.division_name}
              </div>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ color: MUTED, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    <th style={{ ...th, textAlign: 'left', paddingLeft: 20 }}>Team</th>
                    <th style={th} title="Games played">GP</th>
                    <th style={th} title="Wins">W</th>
                    <th style={th} title="Regulation losses">L</th>
                    <th style={th} title="Overtime/shootout losses">OTL</th>
                    <th style={th} title="Ties">T</th>
                    {showGoals && <th style={th} title="Goals for">GF</th>}
                    {showGoals && <th style={th} title="Goals against">GA</th>}
                    {showGoals && <th style={th} title="Goal differential">DIFF</th>}
                    <th style={{ ...th, paddingRight: 20 }} title="Points">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {d.teams.map((t, i) => (
                    <tr key={t.team_id} style={{ borderTop: `1px solid ${LINE}`, background: i % 2 ? SURFACE : '#fff' }}>
                      <td style={{ ...td, textAlign: 'left', paddingLeft: 20 }}>
                        <Link to={`/l/${slug}/team/${t.team_id}`} style={{ color: INK, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ width: 18, color: i < 1 ? accent : MUTED, fontWeight: 800, fontSize: 12 }}>{i + 1}</span>
                          {t.team_name}
                        </Link>
                      </td>
                      <td style={td}>{t.gp}</td>
                      <td style={td}>{t.w}</td>
                      <td style={td}>{t.l}</td>
                      <td style={td}>{t.otl}</td>
                      <td style={td}>{t.t}</td>
                      {showGoals && <td style={td}>{t.gf}</td>}
                      {showGoals && <td style={td}>{t.ga}</td>}
                      {showGoals && (
                        <td style={{ ...td, color: t.gd > 0 ? '#10883f' : t.gd < 0 ? '#c0312f' : MUTED, fontWeight: 700 }}>
                          {t.gd > 0 ? `+${t.gd}` : t.gd}
                        </td>
                      )}
                      <td style={{ ...td, paddingRight: 20, fontWeight: 800, fontSize: 15 }}>{t.pts}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
        {standings?.divisions?.length > 0 && (
          <div style={{ fontSize: 12, color: MUTED }}>
            W = wins · L = regulation losses · OTL = overtime/shootout losses · T = ties
            {showGoals && ' · GF/GA = goals for/against'} · standings update as scoresheets are finalized
          </div>
        )}
      </Section>
    </div>
  );
}

const th = { padding: '12px 10px', textAlign: 'center', fontWeight: 800 };
const td = { padding: '11px 10px', textAlign: 'center', color: INK };
